import React, { useState, useMemo, useEffect, useRef } from 'react';

// ----------------------------------------------------------------------
// 1. TYPES & INTERFACES
// ----------------------------------------------------------------------

type TypeID = 'inventor' | 'analyst' | 'creator' | 'alchemist' | 'influencer' | 'consultant';

interface QuestionOption {
  label: string;
  weights: Partial<Record<TypeID, number>>;
}

interface Question {
  id: number;
  text: string;
  options: QuestionOption[];
}

interface RadarData {
  logic: number;      // 論理的思考
  creativity: number; // 創造性
  empathy: number;    // 共感・情緒
  planning: number;   // 計画・管理
  agility: number;    // 速度・トレンド
}

interface DiagnosisResultType {
  id: TypeID;
  name: string;
  catchphrase: string;
  priority: number;
  color: string;
  accentColor: string;
  gradient: string;
  radar: RadarData;
  description: string;
  activity_method: string;
  weakness: string;
  earnings_map: string;
  imagePath: string; // Path to the uploaded image file
}

type ScreenState = 'start' | 'quiz' | 'calculating' | 'result';

// ----------------------------------------------------------------------
// 2. CONSTANTS & DATA
// ----------------------------------------------------------------------

// -- AVATARS (Image Loader) --
const TypeIcon = ({ typeId, className }: { typeId: TypeID; className?: string }) => {
  const imageSrc = DIAGNOSIS_TYPES[typeId].imagePath;

  return (
    <div className={`${className} flex items-center justify-center overflow-hidden`}>
      <img 
        src={imageSrc} 
        alt={typeId} 
        className="w-full h-full object-contain drop-shadow-xl"
        onError={(e) => {
          (e.target as HTMLImageElement).src = 'https://placehold.co/200x200?text=No+Image';
        }}
      />
    </div>
  );
};

// -- Updated Colors based on User Request --
const DIAGNOSIS_TYPES: Record<TypeID, DiagnosisResultType> = {
  inventor: {
    id: 'inventor',
    name: 'AI発明エンジニア',
    catchphrase: '世界を自動化する仕組みの創造主',
    priority: 1,
    // Green
    color: 'bg-emerald-50',
    accentColor: 'text-emerald-600',
    gradient: 'from-emerald-500 to-green-600',
    radar: { logic: 5, creativity: 3, empathy: 2, planning: 5, agility: 3 },
    description: "あなたは感情よりも論理を重視し、パズルのピースが完璧にハマるような「美しい仕組み」を作ることに喜びを感じるエンジニア気質です。「面倒くさい」という感情を原動力に、あらゆる手作業を自動化しようとする執念を持っています。AIを単なるチャット相手ではなく、「機能を持ったパーツ」として捉え、それらを組み合わせて独自のアプリケーションやシステムを発明する才能があります。",
    activity_method: "ノーコードツール（Bubble, Make, GASなど）とAIを組み合わせて、業務効率化ツールやWebアプリを開発しましょう。コードが書けなくても、AIに「設計図」を渡せばシステムは作れます。まずは自分のルーチンワークを全自動化し、その「仕組み」自体を販売したり、企業のDXコンサルタントとして活躍するのが最適解です。",
    weakness: "「機能的であればいい」と考えがちで、UI/UXやユーザーの感情を無視した複雑すぎるシステムを作ってしまうこと。また、細部にこだわりすぎてリリースが遅れる完璧主義も敵です。「まずは動くものを作る」精神を忘れずに。",
    earnings_map: "【稼ぎ方の地図】\n・業務自動化ツールの開発・販売\n・企業向け社内AIシステムの構築\n・SaaSプロダクトの開発",
    imagePath: "/images/inventor.png"
  },
  analyst: {
    id: 'analyst',
    name: 'AI分析マスター',
    catchphrase: 'データから未来を予見する賢者',
    priority: 2,
    // Purple
    color: 'bg-purple-50',
    accentColor: 'text-purple-600',
    gradient: 'from-purple-500 to-violet-600',
    radar: { logic: 5, creativity: 2, empathy: 2, planning: 4, agility: 3 },
    description: "あなたは曖昧な感覚や憶測を嫌い、確固たる「データ」と「事実」のみを信じるリサーチャーです。情報の海からキラリと光る「正解」を見つけ出すことに快感を覚え、納得いくまで調べ尽くす探究心を持っています。人間一人では処理しきれない膨大なデータをAIに読み込ませ、そこから市場のトレンド、隠れた法則、次に流行るものを論理的に予測することができます。",
    activity_method: "PerplexityやChatGPTのデータ解析機能をフル活用し、株式市場、仮想通貨、マーケティングトレンドなどの情報を収集・分析させましょう。単なる情報のまとめではなく、AIが出したデータにあなたの「洞察（インサイト）」を加え、「勝てる予測レポート」としてnoteやBrainで販売するのが勝ちパターンです。",
    weakness: "十分なデータが揃わないと行動できない「分析麻痺」に陥りやすいこと。ビジネスはスピード勝負なので、時には「仮説」の段階で動き出す勇気が必要です。また、理屈っぽくなりすぎて、相手の感情的なニーズを見落とさないように。",
    earnings_map: "【稼ぎ方の地図】\n・市場予測・トレンドレポートの販売\n・データ分析・リサーチ代行\n・投資判断サポート",
    imagePath: "/images/analyst.png"
  },
  creator: {
    id: 'creator',
    name: 'AI幻想クリエイター',
    catchphrase: '妄想を現実に変える視覚の魔術師',
    priority: 3,
    // Blue
    color: 'bg-blue-50',
    accentColor: 'text-blue-600',
    gradient: 'from-blue-500 to-cyan-600',
    radar: { logic: 2, creativity: 5, empathy: 4, planning: 2, agility: 4 },
    description: "あなたは理屈よりも「直感」と「美意識」で世界を捉えるアーティストです。頭の中に常に鮮やかなイメージが渦巻いており、それを具現化することに生きがいを感じます。論理的な整合性よりも「エモさ」や「インパクト」を重視するため、AIが時折生み出す予測不能な表現さえも味方につけ、誰も見たことのない幻想的なアート作品を生み出すことができます。",
    activity_method: "Midjourney、Stable Diffusion、Runwayなどの画像・動画生成AIを駆使して、SNSで視覚的にインパクトのある作品を発信し続けましょう。あなたの役割はAIのオペレーターではなく、AIという「優秀な絵筆」を操る画家です。独自の世界観を確立し、NFTアート、LINEスタンプ、MV制作などでファンを獲得してください。",
    weakness: "気分の浮き沈みが激しく、事務作業やスケジュール管理が極端に苦手です。クリエイティブ以外の業務はツールで自動化するか、得意な人に任せましょう。また、作品作りに没頭しすぎて「収益化」を忘れないよう、販売導線は最初に作っておくことが重要です。",
    earnings_map: "【稼ぎ方の地図】\n・AI画像素材・NFTの販売\n・YouTube/TikTokの動画制作\n・企業のSNSクリエイティブ制作代行",
    imagePath: "/images/creator.png"
  },
  alchemist: {
    id: 'alchemist',
    name: 'AIテキスト錬成家',
    catchphrase: '言葉に魂を吹き込む現代の錬金術師',
    priority: 4,
    // Black/Dark Gray
    color: 'bg-slate-50',
    accentColor: 'text-slate-800',
    gradient: 'from-slate-700 to-gray-900',
    radar: { logic: 3, creativity: 4, empathy: 5, planning: 3, agility: 3 },
    description: "あなたは言葉の持つ力を深く理解し、テキスト一つで人の心を動かすことができるライター・編集者タイプです。AIが出力する文章の「不自然さ」や「冷たさ」に敏感で、そこに体温と魂を吹き込むことができます。単に情報を伝えるだけでなく、ストーリーテリングを用いて読み手の感情を揺さぶり、共感を生み出すことに長けています。AIを「執筆パートナー」として扱い、二人三脚で物語を紡ぐ才能があります。",
    activity_method: "ChatGPTやClaudeと対話しながら、ブログ記事、YouTubeの脚本、セールスレターを作成しましょう。AIに「構成案」や「叩き台」を作らせ、あなたが「感情の機微」や「独自の体験談」を肉付けする役割分担が最強です。自分一人でゼロから書く苦しみから解放され、編集長のような立場で、爆速かつ高品質なコンテンツを量産してください。",
    weakness: "感受性が豊かすぎるため、SNSの批判やネガティブな反応を気にしすぎて手が止まってしまうことがあります。クリエイターとしての自分を守るため、時には情報を遮断することも大切です。また、言葉の細部にこだわりすぎて執筆スピードが落ちないよう注意。",
    earnings_map: "【稼ぎ方の地図】\n・ブログ・アフィリエイト運営\n・電子書籍（Kindle）の出版\n・Webライティング・脚本作成案件",
    imagePath: "/images/alchemist.png"
  },
  influencer: {
    id: 'influencer',
    name: 'AIインフルエンサー',
    catchphrase: 'トレンドを乗りこなす時代の寵児',
    priority: 5,
    // Yellow
    color: 'bg-yellow-50',
    accentColor: 'text-yellow-600',
    gradient: 'from-yellow-400 to-amber-500',
    radar: { logic: 3, creativity: 4, empathy: 4, planning: 3, agility: 5 },
    description: "あなたは「新しいもの好き」で、世の中のトレンドに誰よりも敏感な発信者タイプです。難しい理屈よりも「楽しいか、つまらないか」を重視し、人々を巻き込んでムーブメントを起こすカリスマ性を持っています。AI技術そのものに詳しい必要はありません。最新のAIツールを使って「こんなに面白いことができる！」と大衆に伝え、エンターテイメントとして消費させる天才的なセンスがあります。",
    activity_method: "顔出し不要のAIアバターやボイスチェンジャーを使い、YouTubeやTikTokで「AI×エンタメ」の動画を量産しましょう。流行りの楽曲やミームをAIで生成し、ショート動画で拡散を狙うのが王道です。あなたのキャラクターとAIの技術を掛け合わせ、広告収益や企業案件で稼ぐ現代のスターを目指してください。",
    weakness: "飽きっぽく、一つのことを継続するのが苦手です。バズるコンテンツを作るのは得意ですが、地味な分析や改善作業はおろそかになりがち。数値管理はAIに任せるか、長期的な戦略を立ててくれるパートナーを見つけると安定します。",
    earnings_map: "【稼ぎ方の地図】\n・AIアバターによる動画配信\n・SNS運用・広告収益\n・トレンド情報の有料コミュニティ運営",
    imagePath: "/images/influencer.png"
  },
  consultant: {
    id: 'consultant',
    name: 'AIコンサルタント',
    catchphrase: 'ビジネスとAIを繋ぐ戦略的架け橋',
    priority: 6,
    // Red
    color: 'bg-red-50',
    accentColor: 'text-red-600',
    gradient: 'from-red-500 to-rose-600',
    radar: { logic: 4, creativity: 3, empathy: 4, planning: 4, agility: 4 },
    description: "あなたは特定の業界知識（医療、教育、不動産など）とAI技術を掛け合わせ、現場の課題を解決するソリューションを提案できる戦略家です。自分でコードを書くわけでも、絵を描くわけでもありませんが、「どのAIを使えば利益が出るか」という全体設計図を描く能力に長けています。AI導入に迷う企業や個人の「翻訳者」となり、ビジネスを成功に導くリーダーです。",
    activity_method: "最新のAIツールを広く浅く把握し、「この業界ならこう使える」という具体的なユースケースを企業に提案しましょう。技術的な仕組みよりも「コスト削減効果」や「売上アップ」というベネフィットを語ることが重要です。AI導入支援、社内研修の講師、顧問契約など、あなたの知見を高単価で売るビジネスモデルが最適です。",
    weakness: "アイデアや戦略を語るだけで満足し、実行がおろそかになる「評論家」になりやすいこと。現場の泥臭い作業や、ツールの細かなエラー対応まで責任を持つ覚悟が必要です。実務が得意なエンジニアとチームを組むことで、最強の布陣となります。",
    earnings_map: "【稼ぎ方の地図】\n・企業のAI導入コンサルティング\n・AI活用セミナー・研修講師\n・DX推進プロジェクトのマネジメント",
    imagePath: "/images/consultant.png"
  }
};

const QUESTIONS: Question[] = [
  { id: 1, text: "もし魔法が一つだけ使えるとしたら、どれを選びますか？", options: [
    { label: "頭の中の妄想を、一瞬で「絵や映像」として実体化させる魔法", weights: { creator: 3, influencer: 1 } },
    { label: "どんな相手でも説得・感動させられる「言葉」が湧いてくる魔法", weights: { alchemist: 3, consultant: 1 } },
    { label: "時間を止めて、面倒な宿題や仕事を一瞬で終わらせる魔法", weights: { inventor: 3, analyst: 1 } },
    { label: "世の中の隠された真実や、未来の出来事が見える魔法", weights: { analyst: 3, consultant: 1 } }
  ]},
  { id: 2, text: "文化祭のクラス出し物。あなたはどの係をやりたいですか？", options: [
    { label: "ポスターや看板を描く「装飾・デザイン係」", weights: { creator: 3 } },
    { label: "演劇の台本や、パンフレットの文章を書く「脚本・広報係」", weights: { alchemist: 3 } },
    { label: "必要な材料の予算計算や、当日のスケジュールを組む「進行管理係」", weights: { inventor: 2, analyst: 2 } },
    { label: "「こんな出し物をやろう！」とみんなに提案する「企画・リーダー係」", weights: { consultant: 2, influencer: 2 } }
  ]},
  { id: 3, text: "新しい家具を買いました。組み立て説明書を見てどう思いますか？", options: [
    { label: "読むのが面倒。完成図だけ見て、とりあえず感覚で作ってみる", weights: { creator: 2, influencer: 2 } },
    { label: "説明書の注意書きまでじっくり読んで、ミスがないように進める", weights: { alchemist: 1, analyst: 2 } },
    { label: "部品の構造を理解して、一番効率の良い手順を頭の中で組む", weights: { inventor: 3 } },
    { label: "「誰か得意な人にやってもらおう」と考える", weights: { consultant: 2, influencer: 1 } }
  ]},
  { id: 4, text: "友人の誕生日プレゼント。どうやって選びますか？", options: [
    { label: "パッケージがおしゃれなものや、デザインが優れたものを直感で選ぶ", weights: { creator: 3, influencer: 1 } },
    { label: "相手が好きそうなものをリサーチし、口コミや評価を徹底的に調べる", weights: { analyst: 3 } },
    { label: "心のこもった手紙やメッセージカードを添えることに全力を注ぐ", weights: { alchemist: 3 } },
    { label: "「絶対に使える実用品」や「生活が楽になる便利グッズ」を贈る", weights: { inventor: 2, consultant: 1 } }
  ]},
  { id: 5, text: "あなたはRPG（ロールプレイングゲーム）の勇者です。仲間にするなら誰？", options: [
    { label: "ド派手な魔法で敵を一掃してくれる魔法使い", weights: { creator: 2, influencer: 2 } },
    { label: "地図や敵の情報を分析して、弱点を教えてくれる参謀", weights: { analyst: 3, consultant: 1 } },
    { label: "自動で回復やアイテム収集をしてくれるロボット", weights: { inventor: 3 } },
    { label: "パーティの会話を盛り上げたり、交渉をしてくれる吟遊詩人", weights: { alchemist: 2, consultant: 2 } }
  ]},
  { id: 6, text: "本屋に行きました。一番長く立ち止まるコーナーは？", options: [
    { label: "写真集、画集、デザイン雑誌のコーナー", weights: { creator: 3 } },
    { label: "小説、エッセイ、ノンフィクションのコーナー", weights: { alchemist: 3 } },
    { label: "ビジネス書、投資、トレンド雑誌のコーナー", weights: { analyst: 2, consultant: 2, influencer: 1 } },
    { label: "プログラミング、技術書、仕事術のコーナー", weights: { inventor: 3 } }
  ]},
  { id: 7, text: "カフェで隣の席の人が「仕事が終わらない…」と嘆いています。心の中でどうアドバイスする？", options: [
    { label: "「もっと効率の良いやり方があるはず。ツールを使えばいいのに」", weights: { inventor: 3 } },
    { label: "「気分転換に映画でも見て、インスピレーションを受ければいいのに」", weights: { creator: 2, influencer: 1 } },
    { label: "「まずは現状を整理して、優先順位リストを作るところから始めよう」", weights: { analyst: 2, consultant: 2 } },
    { label: "「大変だなぁ、話を聞いてあげたいなぁ」と同情する", weights: { alchemist: 3 } }
  ]},
  { id: 8, text: "SNSで「バズっている投稿」を見かけました。どこに注目しますか？", options: [
    { label: "写真や動画のインパクト。「映え」ているかどうか", weights: { creator: 2, influencer: 2 } },
    { label: "文章の書き方。「キャッチコピー」や「言葉選びのうまさ」", weights: { alchemist: 3 } },
    { label: "投稿された時間帯や、ハッシュタグの選び方などの「戦略」", weights: { analyst: 2, consultant: 2 } },
    { label: "「これって自動投稿なのかな？」という仕組み", weights: { inventor: 2 } }
  ]},
  { id: 9, text: "子供の頃、図画工作や美術の授業は好きでしたか？", options: [
    { label: "大好き。絵を描いたり粘土をこねたりするのは得意だった", weights: { creator: 3 } },
    { label: "苦手。正解がないからどうしていいかわからなかった", weights: { analyst: 2, inventor: 1 } },
    { label: "作る作業よりも、作品につけるタイトルや物語を考えるのが好きだった", weights: { alchemist: 3 } },
    { label: "決められた手順通りにキットを組み立てるのは好きだった", weights: { inventor: 2 } }
  ]},
  { id: 10, text: "「あ、この看板の文字、誤字だ」と街中で気づくことはありますか？", options: [
    { label: "よくある。言葉の間違いには敏感に気づいてしまう", weights: { alchemist: 3 } },
    { label: "あまりない。全体の雰囲気やデザインの方に目が行く", weights: { creator: 2, influencer: 2 } },
    { label: "意味が通じれば別にいいじゃん、と思う", weights: { inventor: 1, consultant: 1 } },
    { label: "「なぜチェック漏れが起きたんだろう？」と原因を考えてしまう", weights: { analyst: 2, inventor: 1 } }
  ]},
  { id: 11, text: "夏休みの宿題、どうやってこなすタイプでしたか？", options: [
    { label: "最初の日からコツコツ計画通りに進めるタイプ", weights: { analyst: 2, inventor: 1 } },
    { label: "読書感想文や自由研究など、創作系だけ本気出すタイプ", weights: { alchemist: 2, creator: 2 } },
    { label: "最終日にまとめてやるか、答えを写して効率よく終わらせるタイプ", weights: { inventor: 2, consultant: 1 } },
    { label: "「みんなで集まってやろうぜ！」と友人を誘うタイプ", weights: { influencer: 3 } }
  ]},
  { id: 12, text: "人になにかを説明するとき、どうするのが一番得意ですか？", options: [
    { label: "図や絵を描いて、視覚的にイメージさせる", weights: { creator: 3 } },
    { label: "たとえ話やストーリーを使って、文章や言葉で伝える", weights: { alchemist: 3 } },
    { label: "「結論・理由・具体例」の順序で、論理的に話す", weights: { consultant: 2, analyst: 2 } },
    { label: "「とりあえずこれ見て」と、参考資料やURLを送る", weights: { inventor: 2, influencer: 1 } }
  ]},
  { id: 13, text: "あなたが社長なら、どんな会社を作りたいですか？", options: [
    { label: "世の中にない面白いサービスを生み出し、人々を感動させる会社", weights: { influencer: 2, creator: 2 } },
    { label: "社員が週休4日で働けるよう、徹底的に無駄をなくした効率的な会社", weights: { inventor: 3 } },
    { label: "社員のコミュニケーションが活発で、人間関係が良い会社", weights: { alchemist: 2, consultant: 1 } },
    { label: "市場調査に基づき、確実に利益を出し続ける堅実な会社", weights: { analyst: 3, consultant: 1 } }
  ]},
  { id: 14, text: "難しいパズルや知恵の輪を渡されました。どうしますか？", options: [
    { label: "解けるまで意地でもやり続ける。構造を理解するのが好き", weights: { inventor: 3, analyst: 1 } },
    { label: "すぐに飽きてしまう。完成図が綺麗なら見るだけでいい", weights: { creator: 2, influencer: 1 } },
    { label: "YouTubeで「解き方」を検索して、サクッとクリアする", weights: { inventor: 1, analyst: 1 } },
    { label: "「ねえこれ解ける？」と周りの人に話しかけるネタにする", weights: { influencer: 3, consultant: 1 } }
  ]},
  { id: 15, text: "「100ページの資料を読んで要約して」と頼まれたら？", options: [
    { label: "AIに読ませて3行でまとめさせる。自分では読みたくない", weights: { inventor: 2, influencer: 1 } },
    { label: "重要なポイントにマーカーを引きながら、情報を整理して読む", weights: { analyst: 3 } },
    { label: "「つまりこういう物語だよね」と、分かりやすい言葉に変換する", weights: { alchemist: 3 } },
    { label: "「この資料の目的は何？」と、背景にある意図をまず考える", weights: { consultant: 3 } }
  ]},
  { id: 16, text: "スマホのホーム画面、どんな状態ですか？", options: [
    { label: "好きな壁紙にして、アイコンのデザインなどもこだわっている", weights: { creator: 3, influencer: 1 } },
    { label: "よく使うアプリだけを厳選して配置し、通知も切っている", weights: { inventor: 2, analyst: 1 } },
    { label: "フォルダ分けして綺麗に整理整頓されている", weights: { analyst: 2, inventor: 1 } },
    { label: "未読バッジがたくさんついていても、あまり気にしない", weights: { influencer: 2, consultant: 1 } }
  ]},
  { id: 17, text: "映画やドラマを見るとき、何に一番惹かれますか？", options: [
    { label: "映像の美しさ、衣装、CGの迫力などの「世界観」", weights: { creator: 3 } },
    { label: "セリフの良さ、脚本の構成、伏線回収などの「ストーリー」", weights: { alchemist: 3 } },
    { label: "「この撮影どうやってるんだろう？」という裏側の技術", weights: { inventor: 2, creator: 1 } },
    { label: "「この作品は社会にどんな影響を与えるか？」というテーマ性", weights: { consultant: 2, influencer: 1 } }
  ]},
  { id: 18, text: "もしYouTuberになるなら、どんな動画を投稿しますか？", options: [
    { label: "日常を映画のように切り取った、おしゃれなVlog", weights: { creator: 2, influencer: 2 } },
    { label: "自分の考えや体験談を語りかけるトーク中心の動画", weights: { influencer: 3, alchemist: 1 } },
    { label: "「〇〇の便利な使い方」「時短テクニック」などの解説動画", weights: { inventor: 2, consultant: 2 } },
    { label: "データに基づいたランキングや比較検証動画", weights: { analyst: 3 } }
  ]},
  { id: 19, text: "友達の相談に乗っているとき、よく言われるのは？", options: [
    { label: "「言葉にしてくれてスッキリした、癒やされた」", weights: { alchemist: 3 } },
    { label: "「的確な分析だね、解決策が見えたよ」", weights: { consultant: 2, analyst: 2 } },
    { label: "「その視点はなかった！ 面白いね」", weights: { influencer: 2, creator: 1 } },
    { label: "「そんな簡単な方法があったんだ！」", weights: { inventor: 3 } }
  ]},
  { id: 20, text: "最後に質問です。あなたにとって「理想の未来」とは？", options: [
    { label: "想像したことが何でも現実になる、クリエイティブな世界", weights: { creator: 3 } },
    { label: "面倒な労働から解放されて、自由に過ごせる世界", weights: { inventor: 3 } },
    { label: "みんなが自分の個性を発信し、認め合う世界", weights: { influencer: 3, alchemist: 1 } },
    { label: "最適化されたシステムにより、無駄や失敗がない世界", weights: { analyst: 2, consultant: 2 } }
  ]}
];

// ----------------------------------------------------------------------
// 3. COMPONENTS (No External Imports)
// ----------------------------------------------------------------------

// -- Radar Chart Component --
const RadarChart: React.FC<{ data: RadarData; color: string }> = ({ data, color }) => {
  const size = 200;
  const center = size / 2;
  const radius = 70;
  const labels = ["論理", "創造", "共感", "計画", "速度"];
  
  const getPoint = (value: number, index: number, max: number = 5) => {
    const angle = (Math.PI * 2 * index) / 5 - Math.PI / 2;
    const visualValue = Math.max(3, value); 
    const normalizedValue = value >= 5 ? 5.5 : visualValue;
    
    const r = (normalizedValue / max) * radius;
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle)
    };
  };

  const points = [
    data.logic,
    data.creativity,
    data.empathy,
    data.planning,
    data.agility
  ].map((val, i) => getPoint(val, i));

  const pointsString = points.map(p => `${p.x},${p.y}`).join(' ');
  const levels = [1, 2, 3, 4, 5];
  
  return (
    <div className="relative w-48 h-48 mx-auto">
      <svg width="100%" height="100%" viewBox={`0 0 ${size} ${size}`}>
        {/* Background Grids */}
        {levels.map(level => {
          const pts = Array(5).fill(0).map((_, i) => getPoint(level, i, 5));
          const str = pts.map(p => `${p.x},${p.y}`).join(' ');
          return <polygon key={level} points={str} fill="none" stroke="#e2e8f0" strokeWidth="1" />;
        })}
        
        {/* Axes */}
        {Array(5).fill(0).map((_, i) => {
          const pt = getPoint(5, i, 5);
          return <line key={i} x1={center} y1={center} x2={pt.x} y2={pt.y} stroke="#e2e8f0" strokeWidth="1" />;
        })}

        {/* Labels */}
        {labels.map((label, i) => {
          const pt = getPoint(6.5, i, 5); 
          return (
            <text 
              key={i} 
              x={pt.x} 
              y={pt.y} 
              textAnchor="middle" 
              dominantBaseline="middle" 
              className="text-[10px] fill-slate-500 font-bold"
            >
              {label}
            </text>
          );
        })}

        {/* Data Shape */}
        <polygon points={pointsString} fill={color.replace('bg-', '#').replace('-50', '')} fillOpacity="0.5" stroke={color.replace('bg-', '#').replace('-50', '')} strokeWidth="2" />
        
        {/* Data Points */}
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="3" fill={color.replace('bg-', '#').replace('-50', '')} />
        ))}
      </svg>
    </div>
  );
};

// -- Start Screen --
interface StartScreenProps {
  nickname: string;
  setNickname: (name: string) => void;
  onStart: () => void;
}

const StartScreen: React.FC<StartScreenProps> = ({ nickname, setNickname, onStart }) => {
  return (
    <div className="flex flex-col items-center justify-center min-h-[85vh] px-4 text-center space-y-12 animate-fade-in relative z-10">
      
      <div className="space-y-6 relative">
        <div className="inline-block bg-slate-900/5 backdrop-blur-md text-slate-500 text-xs font-semibold px-4 py-1.5 rounded-full border border-slate-200 tracking-widest">
          WIZAI PROJECT v1.4.0
        </div>
        
        <h1 className="text-5xl md:text-7xl font-extrabold text-slate-800 tracking-tighter drop-shadow-sm leading-tight">
          AI適性<br/>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">スキル診断</span>
        </h1>
        
        <p className="text-slate-600 text-sm md:text-lg max-w-lg mx-auto font-medium leading-relaxed">
          あなたの“才能”と“適性AIスキル”を精密解析し、<br/>
          AI時代を勝ち抜く未来を導き出します
        </p>
      </div>

      <div className="w-full max-w-sm space-y-6 bg-white/70 backdrop-blur-xl p-8 rounded-3xl border border-white shadow-2xl shadow-blue-900/10">
        <div className="space-y-2">
          <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Codename</label>
          <input
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="ニックネームを入力"
            className="w-full bg-slate-50 text-slate-900 text-center text-lg font-semibold py-4 rounded-xl border border-slate-200 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder-slate-400 transition-all"
            maxLength={10}
          />
        </div>

        <button
          onClick={onStart}
          disabled={!nickname.trim()}
          className={`w-full py-4 rounded-xl font-bold text-lg tracking-widest transition-all duration-300 transform 
            ${nickname.trim() 
              ? 'bg-gradient-to-r from-slate-900 to-slate-800 text-white shadow-lg hover:shadow-xl hover:-translate-y-1' 
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
        >
          START DIAGNOSIS
        </button>
      </div>
    </div>
  );
};

// -- Question Screen --
interface QuestionScreenProps {
  question: Question;
  totalQuestions: number;
  currentStep: number;
  onAnswer: (option: QuestionOption) => void;
}

const QuestionScreen: React.FC<QuestionScreenProps> = ({ 
  question, 
  totalQuestions, 
  currentStep, 
  onAnswer 
}) => {
  const progress = ((currentStep + 1) / totalQuestions) * 100;

  return (
    <div className="w-full max-w-2xl mx-auto px-4 min-h-[85vh] flex flex-col justify-center animate-fade-in-up">
      {/* Progress Bar Container */}
      <div className="mb-10 w-full max-w-md mx-auto">
        <div className="flex justify-between text-xs font-bold text-slate-400 mb-2 tracking-wider">
          <span>MISSION {currentStep + 1}</span>
          <span>GOAL {totalQuestions}</span>
        </div>
        <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500 ease-out shadow-[0_0_10px_rgba(59,130,246,0.5)]"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question Card */}
      <div className="bg-white/80 backdrop-blur-xl border border-white rounded-3xl p-8 md:p-12 shadow-2xl shadow-slate-200/50 relative overflow-hidden group">
         {/* Badge */}
         <div className="inline-block mb-6 bg-slate-100 text-slate-500 px-3 py-1 text-xs font-bold rounded-full tracking-wide">
           QUESTION {question.id}
         </div>
        
        <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-10 leading-relaxed">
          {question.text}
        </h2>

        <div className="grid grid-cols-1 gap-3">
          {question.options.map((option, index) => (
            <button
              key={index}
              onClick={() => onAnswer(option)}
              className="w-full p-4 md:p-5 rounded-2xl border border-slate-100 bg-white hover:border-blue-200 hover:bg-blue-50/50 text-left transition-all duration-300 flex items-center group/btn shadow-sm hover:shadow-lg hover:-translate-y-0.5"
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-slate-50 text-slate-400 group-hover/btn:bg-blue-500 group-hover/btn:text-white mr-5 flex items-center justify-center text-sm font-bold transition-colors duration-300">
                {String.fromCharCode(65 + index)}
              </div>
              <span className="text-base text-slate-600 group-hover/btn:text-slate-900 font-medium transition-colors">{option.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// -- Result Screen --
interface ResultScreenProps {
  result: DiagnosisResultType;
  nickname: string;
  onReset: () => void;
}

const ResultScreen: React.FC<ResultScreenProps> = ({ result, nickname, onReset }) => {
  const resultRef = useRef<HTMLDivElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Static Tech Background Component
  const TechBackground = () => (
    <svg className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-50" viewBox="0 0 300 300">
      <g transform="translate(150, 150)">
        <circle r="80" fill="none" stroke="#e2e8f0" strokeWidth="1" />
        <circle r="120" fill="none" stroke="#e2e8f0" strokeWidth="0.5" strokeDasharray="4,4" />
        {[...Array(6)].map((_, i) => {
          const angle = (i / 6) * 2 * Math.PI;
          return <circle key={i} cx={Math.cos(angle) * 120} cy={Math.sin(angle) * 120} r="3" fill="#cbd5e1" />;
        })}
      </g>
    </svg>
  );

  const handleDownloadPDF = async () => {
    if (!resultRef.current) return;
    setIsGeneratingPdf(true);

    try {
      const html2canvas = (window as any).html2canvas;
      const jspdf = (window as any).jspdf;

      if (!html2canvas || !jspdf) {
        throw new Error("PDF libraries not loaded");
      }

      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(resultRef.current, {
        scale: 2, 
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
        scrollY: -window.scrollY, 
        windowHeight: document.documentElement.scrollHeight, 
      });

      const imgData = canvas.toDataURL('image/png');
      const { jsPDF } = jspdf;
      
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      const pdfWidth = 210; 
      const pdfHeight = (imgHeight * pdfWidth) / imgWidth;

      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: [pdfWidth, pdfHeight],
      });
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`wizai_diagnosis_${result.id}.pdf`);
    } catch (error) {
      console.error('PDF Generation Failed', error);
      alert('PDFの保存に失敗しました。');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto px-4 pb-12 animate-fade-in font-sans">
      {/* Result Container to Capture */}
      <div ref={resultRef} className="bg-white p-0 overflow-hidden shadow-2xl rounded-none">
        
        {/* Cover Section */}
        <div className={`relative p-12 text-center overflow-hidden bg-gradient-to-br ${result.gradient} text-white`}>
          <div className="absolute top-0 left-0 w-full h-full bg-white opacity-10" style={{backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px'}}></div>
          
          <div className="relative z-10">
            <p className="text-xl font-bold opacity-100 max-w-xl mx-auto leading-relaxed border-b border-white/30 py-4 mb-4">
              {nickname}さんのAI適性診断結果
            </p>
            
            <h1 className="text-4xl md:text-5xl font-extrabold mb-4 tracking-tight">
              {result.name}
            </h1>
            <p className="text-lg font-bold opacity-90">
              "{result.catchphrase}"
            </p>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-8 md:p-12 space-y-12">
          
          {/* Avatar & Radar Chart Split */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
            <div className="relative w-48 h-48">
              <TechBackground />
              <TypeIcon typeId={result.id} className="relative z-10 p-2" />
            </div>
            <div className="w-48 h-48">
               <RadarChart data={result.radar} color={result.color.replace('bg-', '#')} />
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Personality Data */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-2">
                <div className={`w-1 h-6 rounded ${result.color.replace('bg-', 'bg-')}-400`}></div>
                <h3 className="font-bold text-xl text-slate-800">
                  {nickname}さんの個性データ
                </h3>
            </div>
            <p className="text-slate-600 text-base leading-8 text-justify font-medium">
              {result.description}
            </p>
          </div>

          {/* Activity Method */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-2">
                <div className={`w-1 h-6 rounded ${result.color.replace('bg-', 'bg-')}-400`}></div>
                <h3 className="font-bold text-xl text-slate-800">適性タイプを活かした活動方法</h3>
            </div>
            <p className="text-slate-600 text-base leading-8 text-justify font-medium">
              {result.activity_method}
            </p>
          </div>

          {/* Weakness */}
          <div className="bg-red-50 rounded-2xl p-6 border border-red-100">
             <h4 className="text-red-800 font-bold mb-3 flex items-center">
               <span className="text-xl mr-2">⚠️</span> 弱点・落とし穴
             </h4>
             <p className="text-red-700 text-sm leading-7">
               {result.weakness}
             </p>
          </div>

          {/* Monetization Box */}
          <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
             <div className="text-center mb-6">
               <span className="inline-block px-3 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full mb-2">MONETIZATION</span>
               <h3 className="font-bold text-xl text-slate-800">
                 適性スキルで<span className="border-b-4 border-yellow-200">100万円稼ぐ</span>アイデア
               </h3>
             </div>
            <p className="text-slate-700 text-sm leading-7 text-justify font-medium whitespace-pre-wrap text-center">
              {result.earnings_map}
            </p>
          </div>

          {/* Footer */}
          <div className="text-center text-slate-300 text-[10px] font-mono pt-8">
            WIZAI // AI SKILL DIAGNOSIS REPORT
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col md:flex-row gap-4 justify-center mt-12 px-4 max-w-2xl mx-auto">
        <button
          onClick={handleDownloadPDF}
          disabled={isGeneratingPdf}
          className="flex-1 py-4 bg-slate-900 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all flex items-center justify-center disabled:opacity-50"
        >
          {isGeneratingPdf ? (
            <span className="flex items-center animate-pulse">
              Generating...
            </span>
          ) : (
            <>
              <span className="mr-2 text-xl">↓</span> PDFに保存
            </>
          )}
        </button>
        
        <button
          onClick={onReset}
          className="flex-1 py-4 bg-white text-slate-900 border border-slate-200 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all flex items-center justify-center"
        >
          <span className="mr-2 text-xl">↺</span> スタート画面に戻る
        </button>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------
// 4. MAIN APP CONTROLLER
// ----------------------------------------------------------------------

export default function App() {
  const [screen, setScreen] = useState<ScreenState>('start');
  const [nickname, setNickname] = useState('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  
  // Scores state for 6 new types
  const [scores, setScores] = useState<Record<TypeID, number>>({
    inventor: 0, analyst: 0, creator: 0, alchemist: 0, influencer: 0, consultant: 0
  });

  const handleStart = () => {
    setScreen('quiz');
  };

  const handleAnswer = (option: QuestionOption) => {
    // Add weighted scores
    const newScores = { ...scores };
    Object.entries(option.weights).forEach(([type, weight]) => {
      if (type in newScores) {
        newScores[type as TypeID] += weight || 0;
      }
    });
    setScores(newScores);

    if (currentQuestionIndex < QUESTIONS.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      calculateResult();
    }
  };

  const calculateResult = () => {
    setScreen('calculating');
    setTimeout(() => {
      setScreen('result');
    }, 2000);
  };

  const winnerId = useMemo(() => {
    const types = (Object.keys(scores) as TypeID[]);
    const sortedTypes = types.sort((a, b) => {
      const scoreDiff = scores[b] - scores[a];
      if (scoreDiff !== 0) return scoreDiff;
      
      // Tie-breaker: Lower priority number wins (1 is highest priority)
      return DIAGNOSIS_TYPES[a].priority - DIAGNOSIS_TYPES[b].priority;
    });
    return sortedTypes[0];
  }, [scores]);

  const resetDiagnosis = () => {
    setScores({ inventor: 0, analyst: 0, creator: 0, alchemist: 0, influencer: 0, consultant: 0 });
    setCurrentQuestionIndex(0);
    setScreen('start');
    setNickname('');
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-800 font-sans selection:bg-blue-100 selection:text-blue-900 overflow-x-hidden">
      <style>{`
        @keyframes fade-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fade-in 0.8s cubic-bezier(0.2, 0.8, 0.2, 1); }
        .animate-fade-in-up { animation: fade-in 0.5s ease-out; }
      `}</style>

      {/* Dynamic Aurora Background */}
      <div className="fixed top-0 left-0 w-full h-full pointer-events-none z-0 overflow-hidden">
         <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-blue-200/40 rounded-full blur-[100px] animate-pulse"></div>
         <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-purple-200/40 rounded-full blur-[100px] animate-pulse delay-1000"></div>
         <div className="absolute top-[20%] right-[20%] w-[30%] h-[30%] bg-cyan-100/40 rounded-full blur-[80px] animate-pulse delay-500"></div>
      </div>

      <main className="relative z-10 w-full pt-12 pb-16">
        {screen === 'start' && (
          <StartScreen 
            nickname={nickname} 
            setNickname={setNickname} 
            onStart={handleStart} 
          />
        )}
        {screen === 'quiz' && (
          <QuestionScreen 
            question={QUESTIONS[currentQuestionIndex]}
            totalQuestions={QUESTIONS.length}
            currentStep={currentQuestionIndex}
            onAnswer={handleAnswer}
          />
        )}
        {screen === 'calculating' && (
          <div className="flex flex-col items-center justify-center min-h-[80vh] animate-fade-in">
             <div className="relative w-24 h-24">
                <div className="absolute top-0 left-0 w-full h-full border-4 border-slate-100 rounded-full"></div>
                <div className="absolute top-0 left-0 w-full h-full border-4 border-blue-500 rounded-full border-t-transparent animate-spin"></div>
             </div>
            <h2 className="text-xl font-bold tracking-widest mt-8 text-slate-400 uppercase">診断中...</h2>
          </div>
        )}
        {screen === 'result' && (
          <ResultScreen 
            result={DIAGNOSIS_TYPES[winnerId]} 
            nickname={nickname}
            onReset={resetDiagnosis}
          />
        )}
      </main>

      <footer className="fixed bottom-0 w-full py-4 text-center text-[10px] text-slate-400 pointer-events-none z-50 tracking-widest">
        WIZAI PROJECT &copy; 2024
      </footer>
    </div>
  );
}